import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";

const ACTION_STYLES = {
  primary:
    "group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_35px_-6px_rgba(255,107,0,0.7)] hover:shadow-[0_0_45px_-4px_rgba(0,122,255,0.8)] hover:scale-[1.04] transition-all duration-300",
  secondary:
    "group inline-flex items-center gap-2 rounded-full px-6 py-4 text-sm font-semibold text-white/80 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300",
};

/**
 * Hero-Button. `to` erzeugt einen Router-Link, `href` einen Anker - so kann
 * jede Seite selbst entscheiden, ob der Haupt-Button auf eine andere Seite
 * führt oder zu einem Abschnitt weiter unten springt. `icon` überschreibt das
 * Standardsymbol.
 */
const HeroAction = ({ action, variant, testId }) => {
  const Icon = action.icon ?? (variant === "primary" ? ArrowUpRight : null);
  const body = (
    <>
      {action.label}
      {Icon && (
        <Icon
          className={`w-4 h-4 transition-transform duration-300 ${
            Icon === ArrowUpRight
              ? "group-hover:rotate-45"
              : "group-hover:translate-y-0.5"
          }`}
        />
      )}
    </>
  );

  return action.to ? (
    <Link to={action.to} data-testid={testId} className={ACTION_STYLES[variant]}>
      {body}
    </Link>
  ) : (
    <a href={action.href} data-testid={testId} className={ACTION_STYLES[variant]}>
      {body}
    </a>
  );
};

/**
 * Kopfbereich einer Unterseite. Greift die Bildsprache des Startseiten-Hero auf
 * (Portal-Glow, Raster, maskierte Zeilen), fällt aber flacher aus, damit der
 * Einstieg in den Inhalt schneller passiert.
 *
 * `titleLines` sind die neutralen Zeilen, `titleAccent` die letzte Zeile im
 * Signaturverlauf.
 */
export const PageHero = ({
  id = "top",
  eyebrow,
  titleLines = [],
  titleAccent,
  lead,
  primary,
  secondary,
  chips = [],
  chipsLabel,
  // Schriftgröße der Überschrift als Anteil der Spaltenbreite (cqi). 13 passt
  // für Zeilen bis etwa 7,4-fache Schriftgröße; Seiten mit längeren Zeilen
  // setzen einen kleineren Wert (Preisstruktur, Über uns: 10.4).
  titleFit = 13,
  // Füllt mindestens einen Bildschirm, damit der folgende Abschnitt beim
  // Scroll-Anfang nicht angeschnitten unten hereinragt.
  fullHeight = false,
  children,
}) => {
  return (
    <section
      id={id}
      data-testid="page-hero"
      className={`relative pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden ${
        fullHeight ? "min-h-[calc(100vh-var(--bar-h))] flex items-center" : ""
      }`}
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-brand-orange/20 blur-[140px] animate-pulseGlow" />
        <div className="absolute top-10 right-0 w-[520px] h-[520px] rounded-full bg-brand-blue/20 blur-[150px] animate-pulseGlow" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage:
              "radial-gradient(ellipse 80% 60% at 40% 30%, #000 30%, transparent 75%)",
          }}
        />
      </div>

      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div
            className={`${children ? "lg:col-span-7" : "lg:col-span-9"} [container-type:inline-size]`}
          >
            {eyebrow && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 mb-8"
              >
                <span className="w-2 h-2 rounded-full bg-brand-orange shadow-[0_0_10px_2px_rgba(255,107,0,0.8)]" />
                <span className="text-xs md:text-sm tracking-[0.15em] uppercase font-bold text-white/70">
                  {eyebrow}
                </span>
              </motion.div>
            )}

            {/* Die Zeilen sind fest gesetzt. Die Schrift richtet sich nach der
                Spaltenbreite (cqi, siehe titleFit), gedeckelt je Breakpoint,
                damit keine Zeile umbricht und Wörter einzeln stehen. */}
            <h1
              style={{ "--title-fit": `${titleFit}cqi` }}
              className="font-display font-black tracking-tighter text-[length:min(var(--title-fit),2.25rem)] md:text-[length:min(var(--title-fit),3rem)] lg:text-[length:min(var(--title-fit),3.75rem)] xl:text-[length:min(var(--title-fit),4.5rem)] leading-[0.98]"
            >
              <MaskedLines lines={titleLines} delay={0.2} />
              {titleAccent && (
                <span className="line-mask block">
                  <motion.span
                    className="block gradient-text"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{
                      duration: 1,
                      ease: [0.16, 1, 0.3, 1],
                      delay: 0.2 + titleLines.length * 0.12,
                    }}
                  >
                    {titleAccent}
                  </motion.span>
                </span>
              )}
            </h1>

            {lead && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-white/60"
              >
                {lead}
              </motion.p>
            )}

            {(primary || secondary) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.8 }}
                className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
              >
                {primary && (
                  <HeroAction
                    action={primary}
                    variant="primary"
                    testId="page-hero-primary-cta"
                  />
                )}
                {secondary && (
                  <HeroAction
                    action={secondary}
                    variant="secondary"
                    testId="page-hero-secondary-cta"
                  />
                )}
              </motion.div>
            )}

            {chips.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.05, duration: 0.8 }}
                className="mt-12"
              >
                {chipsLabel && (
                  <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/35 mb-4">
                    {chipsLabel}
                  </p>
                )}
                <ul className="flex flex-wrap gap-2">
                  {chips.map((chip) => (
                    <li
                      key={chip}
                      className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs font-medium text-white/55"
                    >
                      {chip}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

          </div>

          {children && (
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.45, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5"
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
